import React, { useState, useMemo, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useEntries } from "../hooks/useEntries";
import { useNavigate } from "react-router-dom";
import { t } from "../i18n";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { getCurrencySymbol, formatAmount } from "../config/currencies";
import { useHuchas } from "../hooks/useHuchas";
import {
  BarChart3, Wallet, TrendingDown, TrendingUp, Landmark, Target, AlertTriangle,
  Zap, PiggyBank, Calendar, Repeat, Inbox, ChevronDown, LayoutDashboard, ArrowUpDown,
  Plus, ScanLine,
} from "lucide-react";
import { scanReceiptImage } from "./receiptOcr";

function getThemeColors() {
  const s = getComputedStyle(document.documentElement);
  const get = v => s.getPropertyValue(v).trim();
  return {
    income:  get("--green")    || "#16a34a",
    expense: get("--red")      || "#dc2626",
    balance: get("--blue")     || "#2563eb",
    fund:    get("--purple")   || "#7c3aed",
    grid:    get("--gray-100") || "#f3f4f6",
  };
}

// Una "operación combinada" (ingreso + gasto juntos, ej. venta con coste)
// se guarda con el NETO en amount/type — el bruto de cada lado vive en
// incomeAmount/expenseAmount. Sumar solo por type/amount (como se hacía
// antes en toda la app) perdía silenciosamente el lado gasto de cualquier
// combinada con neto positivo (y el lado ingreso de las de neto negativo).
function getIncomePart(e) {
  if (e.isCombined) return e.incomeAmount || 0;
  return e.type === "income" ? (e.amount || 0) : 0;
}
function getExpensePart(e) {
  if (e.isCombined) return e.expenseAmount || 0;
  return e.type === "expense" ? (e.amount || 0) : 0;
}

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const MONTHS_EN = ["January","February","March","April","May","June",
                   "July","August","September","October","November","December"];

export default function Panel() {
  const { profile, lang, currency, theme, updateConfig } = useAuth();
  const { visibleEntries, loading, isPro, FREE_LIMITS, thisMonthCount } = useEntries();
  const { huchas, getProgress } = useHuchas();
  const navigate = useNavigate();
  const [clr, setClr] = useState(getThemeColors);
  useEffect(() => { setClr(getThemeColors()); }, [theme]);

  // Escaneo rápido de tickets desde el panel: hace el OCR aquí mismo y
  // lleva a Añadir con los campos ya rellenos (mismo flujo de "revisar
  // antes de guardar" que el escaneo dentro del propio formulario — ver
  // receiptOcr.js y el useEffect que lo recoge en Anadir.js).
  const [quickScanning, setQuickScanning] = useState(false);
  const quickScanInputRef = useRef(null);

  async function handleQuickScan(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setQuickScanning(true);
    try {
      const result = await scanReceiptImage(file);
      navigate("/anadir", { state: { scanResult: result } });
    } catch (err) {
      console.error("Error al escanear el ticket:", err);
      navigate("/anadir", { state: { scanError: true } });
    } finally {
      setQuickScanning(false);
      e.target.value = "";
    }
  }

  const c = profile?.config || {};
  const pct = c.fund_pct !== undefined ? c.fund_pct : 10;
  const accounts = c.accounts || [];
  const budgets = profile?.config?.budgets || {};
  const budgetColors = profile?.config?.budget_colors || {};

  const COLOR_PALETTE = [
    { id:"theme",  color: null },
    { id:"green",  color:"#16a34a" },
    { id:"blue",   color:"#2563eb" },
    { id:"purple", color:"#7c3aed" },
    { id:"amber",  color:"#d97706" },
    { id:"red",    color:"#dc2626" },
    { id:"pink",   color:"#db2777" },
    { id:"cyan",   color:"#0891b2" },
  ];

  function getBarColor(cat) {
    const colorId = budgetColors[cat] || "theme";
    const found = COLOR_PALETTE.find(c => c.id === colorId);
    return found?.color || clr.balance;
  }
  const budgetLabel = c.budget_label || (lang==="en"?"Budgets":"Presupuestos");
  const ps = c.panel_sections || { grafico:true, balance:true, presupuestos:true, cuentas:true, historial:true, huchas:true };
  const sym = getCurrencySymbol(currency);
  const T = (key) => t(lang, key);
  const MONTH_LIST = lang === "en" ? MONTHS_EN : MONTHS;
  const fmt = n => formatAmount(n, currency);

  const [filterYear,  setFilterYear]  = useState(String(new Date().getFullYear()));
  const [filterMonth, setFilterMonth] = useState(MONTHS[new Date().getMonth()]);
  const [expanded, setExpanded] = useState(null);
  // "ambos" | "ingresos" | "gastos" | "barras"
  // "ingresos" | "gastos" | "barras" — se recuerda entre sesiones,
  // guardado en el perfil (config.panel_chart_mode), igual que el tema.
  const [chartMode, setChartModeLocal] = useState(c.panel_chart_mode || "ingresos");
  function setChartMode(mode) {
    setChartModeLocal(mode);
    updateConfig({ ...c, panel_chart_mode: mode });
  }
  // Popover de detalle al clicar un gráfico: { x, y, title, value, color, filter } | null
  const [popover, setPopover] = useState(null);

  function goToFiltered(filter = {}, path = "/historial") {
    const params = new URLSearchParams();
    if (filter.year)  params.set("year", filter.year);
    if (filter.month) params.set("month", filter.month);
    if (filter.type)  params.set("type", filter.type);
    if (filter.cat)   params.set("cat", filter.cat);
    setPopover(null);
    navigate(`${path}?${params.toString()}`);
  }

  function showPopover(clientX, clientY, { title, value, color, filter, path = "/historial" }) {
    setPopover({ x: clientX, y: clientY, title, value, color, filter, path });
  }

  const years = useMemo(() => {
    const ys = [...new Set(visibleEntries.map(e=>e.year))].sort();
    return ys.length ? ys : [new Date().getFullYear()];
  },[visibleEntries]);

  const filtered = useMemo(() => visibleEntries.filter(e =>
    (filterYear==="Todos" || e.year===parseInt(filterYear)) &&
    (filterMonth==="Todos" || e.month===filterMonth)
  ),[visibleEntries,filterYear,filterMonth]);

  const inc = filtered.reduce((s,e)=>s+getIncomePart(e),0);
  const exp = filtered.reduce((s,e)=>s+getExpensePart(e),0);
  const bal = inc - exp;
  const inv = bal * (pct/100);
  const lib = bal * (1 - pct/100);

  // Disponible del mes anterior al filtro actual, para mostrar la variación
  // ("+18,4% vs. mes anterior") en la tarjeta de saldo destacada. Si el
  // filtro está en "Todos" (sin mes/año concreto), no hay un "mes anterior"
  // bien definido, así que se omite la comparación en ese caso.
  const prevLib = useMemo(() => {
    if (filterMonth === "Todos" || filterYear === "Todos") return null;
    const idx = MONTH_LIST.indexOf(filterMonth);
    if (idx === -1) return null;
    const prevIdx = idx === 0 ? 11 : idx - 1;
    const prevYear = idx === 0 ? parseInt(filterYear) - 1 : parseInt(filterYear);
    const prevMonthName = MONTH_LIST[prevIdx];
    const prevEntries = visibleEntries.filter(e => e.month === prevMonthName && e.year === prevYear);
    const prevInc = prevEntries.reduce((s,e)=>s+getIncomePart(e),0);
    const prevExp = prevEntries.reduce((s,e)=>s+getExpensePart(e),0);
    const prevBal = prevInc - prevExp;
    return prevBal * (1 - pct/100);
  }, [visibleEntries, filterMonth, filterYear, pct, MONTH_LIST]);

  // Porcentaje de variación del disponible respecto al mes anterior.
  // null si no hay datos del mes anterior, o si este es cero (división por
  // cero no tiene un porcentaje significativo).
  const libChangePct = useMemo(() => {
    if (prevLib === null || prevLib === 0) return null;
    return ((lib - prevLib) / Math.abs(prevLib)) * 100;
  }, [lib, prevLib]);

  const pieData = useMemo(() => {
    if (inc === 0 && exp === 0) return [];
    return [
      { name: c.income_label || T("income_label"), value: inc, color: clr.income },
      { name: c.expense_label || T("expense_label"), value: exp, color: clr.expense },
    ];
  }, [inc, exp, clr]);

  const expByCategory = useMemo(() => {
    const result = {};
    filtered.forEach(e => {
      const part = getExpensePart(e);
      if (part <= 0) return;
      const cat = e.cat || "General";
      result[cat] = (result[cat] || 0) + part;
    });
    return result;
  }, [filtered]);

  const budgetItems = useMemo(() => {
    return Object.entries(budgets).map(([cat, limit]) => ({
      cat,
      limit: parseFloat(limit),
      spent: expByCategory[cat] || 0,
      pct: Math.min(100, Math.round(((expByCategory[cat] || 0) / parseFloat(limit)) * 100)),
    })).filter(b => b.limit > 0);
  }, [budgets, expByCategory]);

  const monthRows = useMemo(() => {
    const keys = [...new Set(filtered.map(e=>`${e.year}-${e.month}`))];
    return keys.sort((a,b)=>{
      const [ay,am]=a.split("-"); const [by,bm]=b.split("-");
      return ay!==by ? by-ay : MONTHS.indexOf(bm)-MONTHS.indexOf(am);
    }).map(k => {
      const [y,m] = k.split("-");
      const rows  = filtered.filter(e=>String(e.year)===y && e.month===m);
      const ri    = rows.reduce((s,e)=>s+getIncomePart(e),0);
      const rx    = rows.reduce((s,e)=>s+getExpensePart(e),0);
      const rb    = ri-rx;
      return { year:y, month:m, inc:ri, exp:rx, bal:rb, inv:rb*(pct/100), lib:rb*(1-pct/100) };
    });
  },[filtered,pct]);

  // Movimientos del mes actual ordenados por fecha desc
  const currentMonthEntries = useMemo(() => {
    const now = new Date();
    const cm = MONTHS[now.getMonth()];
    const cy = now.getFullYear();
    return visibleEntries
      .filter(e => e.month === cm && e.year === cy)
      .sort((a,b) => {
        if (a.datetime && b.datetime) return b.datetime.localeCompare(a.datetime);
        return 0;
      });
  }, [visibleEntries]);

  // Datos para mini gráfico de barras (últimos 6 meses)
  const barData = useMemo(() => {
    const keys = [...new Set(visibleEntries.map(e=>`${e.year}-${e.month}`))];
    return keys.sort((a,b)=>{
      const [ay,am]=a.split("-"); const [by,bm]=b.split("-");
      return ay!==by?ay-by:MONTHS.indexOf(am)-MONTHS.indexOf(bm);
    }).slice(-6).map(k=>{
      const [y,m]=k.split("-");
      const rows=visibleEntries.filter(e=>String(e.year)===y&&e.month===m);
      const ri=rows.reduce((s,e)=>s+getIncomePart(e),0);
      const rx=rows.reduce((s,e)=>s+getExpensePart(e),0);
      return { label: m.slice(0,3), year: parseInt(y), month: m, inc: ri, exp: rx };
    });
  },[visibleEntries]);

  if (loading) return <div className="page"><p style={{color:"var(--gray-400)"}}>Cargando...</p></div>;

  const usedPct = Math.min(100, Math.round((thisMonthCount/FREE_LIMITS.entriesPerMonth)*100));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{display:"flex",alignItems:"center",gap:8}}><LayoutDashboard size={22}/> {T("panel_title")}</h2>
          <p className="page-sub">{T("panel_sub")} {c.income_label?.toLowerCase()} y {c.expense_label?.toLowerCase()}</p>
        </div>
        <div style={{display:"flex",gap:8}}>
          <input ref={quickScanInputRef} type="file" accept="image/*" capture="environment"
                 style={{display:"none"}} onChange={handleQuickScan} />
          <button className="btn btn-outline" disabled={quickScanning}
                  title={lang==="en" ? "Scan receipt" : "Escanear ticket"}
                  aria-label={lang==="en" ? "Scan receipt" : "Escanear ticket"}
                  style={{width:40,height:40,padding:0,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}
                  onClick={()=>quickScanInputRef.current?.click()}>
            <ScanLine size={18}/>
          </button>
          <button className="btn btn-primary"
                  title={T("panel_add")}
                  aria-label={T("panel_add")}
                  style={{width:40,height:40,padding:0,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}
                  onClick={()=>navigate("/anadir")}>
            <Plus size={20}/>
          </button>
        </div>
      </div>

      {!isPro && (
        <div className="pro-banner" style={{marginBottom:20}}>
          <div>
            <h3>{T("panel_pro_banner_title")}</h3>
            <p>{T("panel_pro_banner_sub")}</p>
          </div>
          <button className="btn btn-pro btn-sm" onClick={()=>navigate("/pro")}>{T("panel_pro_btn")}</button>
        </div>
      )}

      {!isPro && (
        <div className="card card-sm" style={{marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <span style={{fontSize:13,color:"var(--gray-600)"}}>
              {T("panel_entries_month")} <strong>{thisMonthCount} / {FREE_LIMITS.entriesPerMonth}</strong>
            </span>
            {usedPct >= 80 && (
              <button className="btn btn-pro btn-sm" onClick={()=>navigate("/pro")}>{T("panel_expand")}</button>
            )}
          </div>
          <div className="limit-bar">
            <div className={`limit-fill ${usedPct>=100?"full":usedPct>=80?"warn":""}`} style={{width:`${usedPct}%`}} />
          </div>
        </div>
      )}

      <div className="filters">
        <select className="filter-select" value={filterYear} onChange={e=>setFilterYear(e.target.value)}>
          <option value="Todos">{T("panel_all_years")}</option>
          {years.map(y=><option key={y}>{y}</option>)}
        </select>
        <select className="filter-select" value={filterMonth} onChange={e=>setFilterMonth(e.target.value)}>
          <option value="Todos">{T("panel_all_months")}</option>
          {MONTHS.map((m,i)=><option key={m} value={m}>{MONTH_LIST[i]}</option>)}
        </select>
      </div>

      {/* (El bloque de gráfico se movió justo debajo de "Saldo disponible";
          ver más abajo, después del bloque SALDO TOTAL) */}

      {/* SALDO TOTAL — tarjeta destacada, fondo oscuro fijo en cualquier tema.
          Muestra el DISPONIBLE (tras descontar % de ahorro/inversión), con
          variación real vs. el mes anterior y un mini-sparkline de los
          últimos 6 meses. */}
      {ps.balance !== false && (() => {
        const sparkValues = barData.map(b => (b.inc - b.exp) * (1 - pct/100));
        const sparkMin = Math.min(...sparkValues, 0);
        const sparkMax = Math.max(...sparkValues, 1);
        const sparkRange = sparkMax - sparkMin || 1;
        const sparkW = 120, sparkH = 44;
        const sparkPoints = sparkValues.map((v,i) => {
          const x = sparkValues.length > 1 ? (i/(sparkValues.length-1)) * sparkW : sparkW/2;
          const y = sparkH - ((v - sparkMin) / sparkRange) * sparkH;
          return `${x},${y}`;
        }).join(" ");

        return (
        <div className="balance-hero" style={{marginBottom:20}}>
          <div className="balance-hero-top">
            <div>
              <div className="balance-hero-label">
                {lang==="en" ? "Available balance" : "Saldo disponible"}
              </div>
              <div className="balance-hero-value">
                {fmt(lib)}
                {libChangePct !== null && (
                  <span className={`balance-hero-arrow ${libChangePct>=0?"up":"down"}`} style={{display:"inline-flex",alignItems:"center"}}>
                    {libChangePct>=0 ? <TrendingUp size={16}/> : <TrendingDown size={16}/>}
                  </span>
                )}
              </div>
              <div className={`balance-hero-sub ${libChangePct===null?"":(libChangePct>=0?"up":"down")}`}>
                {libChangePct !== null
                  ? `${libChangePct>=0?"+":""}${libChangePct.toFixed(1)}% ${lang==="en"?"vs. last month":"vs. mes anterior"}`
                  : `${lang==="en"?"Available":"Disponible"} (${100-pct}%) · ${filterMonth==="Todos"?(lang==="en"?"All time":"Todo"):filterMonth} ${filterYear!=="Todos"?filterYear:""}`}
              </div>
            </div>
            {sparkValues.length > 1 && (
              <svg className="balance-hero-spark" width={sparkW} height={sparkH} viewBox={`0 0 ${sparkW} ${sparkH}`} preserveAspectRatio="none">
                <polyline points={sparkPoints} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        </div>
        );
      })()}

      {/* GRÁFICO — movido aquí, justo debajo del saldo disponible. Solo
          3 modos (Ingresos / Gastos / Barras, sin "Ambos"), preferencia
          guardada en el perfil (config.panel_chart_mode). */}
      {ps.grafico !== false && (
        <div className="card" style={{marginBottom:20,padding:"18px 22px"}}>
          <div style={{display:"flex",gap:6,marginBottom:16}}>
            {[
              ["ingresos", c.income_label  || (lang==="en"?"Income":"Ingresos")],
              ["gastos",   c.expense_label || (lang==="en"?"Expenses":"Gastos")],
              ["barras",   lang==="en"?"Bars":"Barras"],
            ].map(([mode, label]) => (
              <button key={mode} onClick={()=>setChartMode(mode)}
                style={{
                  flex:1, padding:"8px 10px", borderRadius:10, border:"none", cursor:"pointer",
                  fontSize:13, fontWeight:600,
                  background: chartMode===mode ? "var(--blue)" : "var(--gray-100)",
                  color: chartMode===mode ? "white" : "var(--gray-500)",
                  transition:"all .15s",
                }}>
                {label}
              </button>
            ))}
          </div>

          {pieData.length === 0 ? (
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                         color:"var(--gray-400)",fontSize:13,padding:"24px 0"}}>
              <span style={{display:"flex"}}><BarChart3 size={28}/></span>
              <span>{lang==="en"?"No data for this period":"Sin datos para este período"}</span>
            </div>
          ) : chartMode === "barras" ? (
            /* ── MODO BARRAS: estilo moderno, sin leyenda manual aparte ── */
            <div>
              <div style={{fontSize:12,color:"var(--gray-400)",marginBottom:8}}>
                {lang==="en"?"Last 6 months":"Últimos 6 meses"}
              </div>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={barData} margin={{top:0,right:0,left:0,bottom:0}} barSize={14}>
                  <CartesianGrid strokeDasharray="3 3" stroke={clr.grid} vertical={false}/>
                  <XAxis dataKey="label" tick={{fontSize:11,fill:"var(--gray-400)"}} axisLine={false} tickLine={false}/>
                  <Tooltip
                    formatter={(v,n)=>[fmt(v), n==="inc"?(c.income_label||T("income_label")):(c.expense_label||T("expense_label"))]}
                    contentStyle={{background:"var(--card)",border:"1px solid var(--border)",borderRadius:8,fontSize:12}}
                    cursor={false}/>
                  <Bar dataKey="inc" fill={clr.income} radius={[5,5,0,0]} cursor="pointer"
                       onClick={(data,index,event)=>showPopover(event.clientX, event.clientY, {
                         title: `${c.income_label||T("income_label")} · ${data.label}`,
                         value: fmt(data.inc), color: clr.income,
                         filter: { year: data.year, month: data.month, type: "income" }
                       })}/>
                  <Bar dataKey="exp" fill={clr.expense} radius={[5,5,0,0]} cursor="pointer"
                       onClick={(data,index,event)=>showPopover(event.clientX, event.clientY, {
                         title: `${c.expense_label||T("expense_label")} · ${data.label}`,
                         value: fmt(data.exp), color: clr.expense,
                         filter: { year: data.year, month: data.month, type: "expense" }
                       })}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            /* ── MODO ÚNICO (Ingresos o Gastos): donut limpio + desglose,
               estilo moderno (números grandes, sin texto diminuto dentro
               del donut). ── */
            (() => {
              const isIncome = chartMode === "ingresos";
              const val   = isIncome ? inc : exp;
              const other = isIncome ? exp : inc;
              const color = isIncome ? clr.income : clr.expense;
              const label = isIncome
                ? (c.income_label  || T("income_label"))
                : (c.expense_label || T("expense_label"));
              const otherLabel = isIncome
                ? (c.expense_label || T("expense_label"))
                : (c.income_label  || T("income_label"));
              const otherColor = isIncome ? clr.expense : clr.income;
              return (
                <div style={{display:"flex",alignItems:"center",gap:24,flexWrap:"wrap"}}>
                  <div style={{position:"relative",width:120,height:120,flexShrink:0,
                               filter:`drop-shadow(0 4px 10px color-mix(in srgb, ${color} 40%, transparent))`}}>
                    <ResponsiveContainer width={120} height={120}>
                      <PieChart>
                        <Pie data={[{value:val},{value:Math.max(0,other)}]}
                             cx="50%" cy="50%" innerRadius={40} outerRadius={58}
                             startAngle={90} endAngle={-270} dataKey="value" paddingAngle={3}>
                          <Cell fill={color} cursor="pointer"
                                onClick={(e)=>showPopover(e.clientX, e.clientY, {
                                  title: label,
                                  value: fmt(val), color: color,
                                  filter: { year: filterYear==="Todos"?undefined:filterYear, month: filterMonth==="Todos"?undefined:filterMonth, type: isIncome?"income":"expense" }
                                })}/>
                          <Cell fill="var(--gray-100)"/>
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:14,flex:1,minWidth:140}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                        <span style={{width:8,height:8,borderRadius:"50%",background:color,flexShrink:0}}/>
                        <span style={{fontSize:12,color:"var(--gray-500)"}}>{label}</span>
                      </div>
                      <div style={{fontSize:22,fontWeight:800,color:color,
                                   textShadow:`0 2px 8px color-mix(in srgb, ${color} 35%, transparent)`}}>{fmt(val)}</div>
                    </div>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                        <span style={{width:8,height:8,borderRadius:"50%",background:otherColor,flexShrink:0}}/>
                        <span style={{fontSize:12,color:"var(--gray-500)"}}>{otherLabel}</span>
                      </div>
                      <div style={{fontSize:16,fontWeight:700,color:otherColor,opacity:.85,
                                   textShadow:`0 1px 6px color-mix(in srgb, ${otherColor} 30%, transparent)`}}>{fmt(other)}</div>
                    </div>
                  </div>
                </div>
              );
            })()
          )}
        </div>
      )}

      {/* MÉTRICAS DE BALANCE */}
      {ps.balance !== false && (
        <div style={{marginBottom:20}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10}}>
            <div className="metric-card">
              <div className="metric-label" style={{display:"flex",alignItems:"center",gap:5}}><Wallet size={13}/> {c.income_label || T("income_label")}</div>
              <div className="metric-value green">{fmt(inc)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label" style={{display:"flex",alignItems:"center",gap:5}}><TrendingDown size={13}/> {c.expense_label || T("expense_label")}</div>
              <div className="metric-value red">{fmt(exp)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label" style={{display:"flex",alignItems:"center",gap:5}}><BarChart3 size={13}/> {c.balance_label || T("balance_label")}</div>
              <div className={`metric-value ${bal>=0?"green":"red"}`}>{fmt(bal)}</div>
            </div>
            <div className="metric-card" style={{cursor:"pointer"}}
                 onClick={()=>navigate("/inversiones")}
                 title={lang==="en" ? "Go to Investments" : "Ir a Inversiones"}>
              <div className="metric-label" style={{display:"flex",alignItems:"center",gap:5}}><Landmark size={13}/> {c.fund_label || T("fund_label")} ({pct}%)</div>
              <div className="metric-value purple">{fmt(inv)}</div>
            </div>
          </div>
        </div>
      )}

      {/* PRESUPUESTOS */}
      {ps.presupuestos !== false && budgetItems.length > 0 && (
        <div style={{marginBottom:24}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <h3 style={{fontSize:15,fontWeight:600,color:"var(--gray-700)",display:"flex",alignItems:"center",gap:6}}><Target size={16}/> {budgetLabel}</h3>
            <button className="btn btn-outline btn-sm" onClick={()=>navigate("/presupuestos")}>
              {lang==="en"?"Manage":"Gestionar"}
            </button>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {budgetItems.map(b => (
              <div key={b.cat} className="card card-sm">
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <span style={{display:"flex",alignItems:"center",gap:6,fontSize:13,fontWeight:600,color:"var(--gray-700)"}}>
                    <span style={{width:8,height:8,borderRadius:2,background:b.pct>=100?"var(--red)":b.pct>=80?"var(--amber)":getBarColor(b.cat),flexShrink:0,display:"inline-block"}}/>
                    {b.cat}
                  </span>
                  <span style={{fontSize:13,color:b.pct>=100?"var(--red)":b.pct>=80?"var(--amber)":"var(--gray-500)",display:"inline-flex",alignItems:"center",gap:4}}>
                    {fmt(b.spent)} / {fmt(b.limit)}
                    {b.pct>=100 && <AlertTriangle size={13}/>}{b.pct>=80 && b.pct<100 && <Zap size={13}/>}
                  </span>
                </div>
                <div className="limit-bar">
                  <div style={{
                    height:"100%", borderRadius:99,
                    width:`${b.pct}%`,
                    background: b.pct>=100 ? "var(--red)" : b.pct>=80 ? "var(--amber)" : getBarColor(b.cat),
                    transition:"width .3s"
                  }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* HUCHAS */}
      {ps.huchas !== false && huchas.length > 0 && (
        <div style={{marginBottom:24}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <h3 style={{fontSize:15,fontWeight:600,color:"var(--gray-700)",display:"flex",alignItems:"center",gap:6}}><PiggyBank size={16}/> {lang==="en"?"Savings jars":"Huchas"}</h3>
            <button className="btn btn-outline btn-sm" onClick={()=>navigate("/huchas")}>
              {lang==="en"?"Manage":"Gestionar"}
            </button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,220px),1fr))",gap:10}}>
            {huchas.map(h => {
              const progress = getProgress(h);
              return (
                <div key={h.id} className="card card-sm">
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                    <span style={{fontSize:18}}>{h.icon}</span>
                    <span style={{fontSize:13,fontWeight:600,color:"var(--gray-700)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {h.name}
                    </span>
                  </div>
                  <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:6}}>
                    {h.goal ? `${fmt(h.saldo||0)} / ${fmt(h.goal)}` : fmt(h.saldo||0)}
                  </p>
                  {progress !== null && (
                    <div className="limit-bar">
                      <div style={{height:"100%",borderRadius:99,width:`${progress}%`,
                                   background:progress>=100?"var(--green)":h.color,transition:"width .3s"}}/>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CUENTAS */}
      {ps.cuentas !== false && accounts.length > 0 && (
        <div style={{marginBottom:20}}>
          <h3 style={{fontSize:15,fontWeight:600,color:"var(--gray-700)",marginBottom:10}}>{T("panel_accounts_title")}</h3>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {accounts.map(acc=>{
              // "filtered" ya respeta el mes/año elegido arriba en el Panel
              // (por defecto, el mes en curso) — así estas tarjetas muestran
              // solo el movimiento de ESE periodo, no todo el histórico
              // acumulado (para eso está la página de Cuentas, con su saldo
              // real de siempre).
              const aInc = filtered.filter(e=>e.payment===acc.name).reduce((s,e)=>s+getIncomePart(e),0);
              const aExp = filtered.filter(e=>e.payment===acc.name).reduce((s,e)=>s+getExpensePart(e),0);
              const aNet = aInc - aExp;
              return (
                <div key={acc.name} onClick={()=>navigate("/cuentas")}
                     style={{padding:"12px 16px",borderRadius:"var(--radius)",background:"var(--card)",
                             border:`2px solid ${acc.color}`,cursor:"pointer",minWidth:140,
                             boxShadow:"0 1px 4px rgba(0,0,0,.06)",transition:"transform .15s"}}
                     onMouseEnter={e=>e.currentTarget.style.transform="translateY(-2px)"}
                     onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                    <span style={{color:acc.color,fontSize:14}}>●</span>
                    <span style={{fontSize:13,fontWeight:600,color:"var(--gray-700)"}}>{acc.name}</span>
                  </div>
                  <div style={{fontSize:18,fontWeight:700,color:aNet>=0?"var(--green)":"var(--red)"}}>{fmt(aNet)}</div>
                  <div style={{fontSize:11,color:"var(--gray-400)",marginTop:2,display:"flex",alignItems:"center",gap:6}}>
                    <span style={{display:"inline-flex",alignItems:"center",gap:2}}><TrendingUp size={11}/>{fmt(aInc)}</span>
                    <span style={{display:"inline-flex",alignItems:"center",gap:2}}><TrendingDown size={11}/>{fmt(aExp)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* HISTORIAL — movimientos del mes actual + resumen meses anteriores */}
      {ps.historial !== false && (
        <div>
          {/* Movimientos del mes en curso */}
          {currentMonthEntries.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <h3 style={{fontSize:15,fontWeight:600,color:"var(--gray-700)",display:"flex",alignItems:"center",gap:6}}>
                  <Calendar size={16}/> {MONTH_LIST[new Date().getMonth()]} {new Date().getFullYear()}
                </h3>
                <span style={{fontSize:12,color:"var(--gray-400)"}}>{currentMonthEntries.length} {lang==="en"?"entries":"entradas"}</span>
              </div>
              <div className="card" style={{padding:0,overflow:"hidden"}}>
                <div style={{maxHeight:280,overflowY:"auto"}}>
                  {currentMonthEntries.map((e,i) => (
                    <div key={e.id||i}
                         onClick={()=>navigate(`/historial?entryId=${e.id}`)}
                         style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                                 padding:"11px 16px",borderBottom:"1px solid var(--gray-100)",
                                 cursor:"pointer",transition:"background .12s"}}
                         onMouseEnter={ev=>ev.currentTarget.style.background="var(--gray-50)"}
                         onMouseLeave={ev=>ev.currentTarget.style.background="transparent"}>
                      <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                        <span style={{flexShrink:0,display:"flex",
                                      color:e.isCombined?"var(--blue)":(e.type==="income"?"var(--green)":"var(--red)")}}>
                          {e.isCombined ? <ArrowUpDown size={16}/> : (e.type==="income" ? <TrendingUp size={16}/> : <TrendingDown size={16}/>)}
                        </span>
                        <div style={{minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--gray-800)",
                                       whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                            {e.cat||"General"}{e.desc?` · ${e.desc}`:""}
                          </div>
                          <div style={{fontSize:11,color:"var(--gray-400)"}}>{e.datetime||e.month}</div>
                        </div>
                      </div>
                      {e.isCombined ? (
                        <span style={{fontWeight:700,fontSize:13,flexShrink:0,marginLeft:8,textAlign:"right"}}>
                          {e.incomeAmount > 0 && <span style={{color:"var(--green)"}}>+{fmt(e.incomeAmount)}</span>}
                          {e.incomeAmount > 0 && e.expenseAmount > 0 && " "}
                          {e.expenseAmount > 0 && <span style={{color:"var(--red)"}}>-{fmt(e.expenseAmount)}</span>}
                        </span>
                      ) : (
                        <span style={{fontWeight:700,fontSize:14,color:e.type==="income"?"var(--green)":"var(--red)",flexShrink:0,marginLeft:8}}>
                          {e.type==="income"?"+":"-"}{fmt(e.amount)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Resumen meses anteriores */}
          {monthRows.length === 0 ? (
            <div className="empty">
              <div className="empty-icon"><Inbox size={28}/></div>
              <h3>{T("panel_no_data")}</h3>
              <p>{T("panel_no_data_sub")}</p>
              <button className="btn btn-primary" style={{margin:"16px auto 0",display:"flex"}} onClick={()=>navigate("/anadir")}>
                {T("panel_add")}
              </button>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {monthRows.map((r,i)=>{
                const mIdx = MONTHS.indexOf(r.month);
                const mLabel = mIdx>=0 ? MONTH_LIST[mIdx] : r.month;
                const key = `${r.year}-${r.month}`;
                const isOpen = expanded === key;
                return (
                  <div key={i} onClick={()=>setExpanded(isOpen ? null : key)}
                       style={{background:"var(--card)",borderRadius:"var(--radius)",border:"1px solid var(--border)",
                               boxShadow:"0 1px 4px rgba(0,0,0,.05)",cursor:"pointer",overflow:"hidden",transition:"box-shadow .15s"}}
                       onMouseEnter={e=>e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,.1)"}
                       onMouseLeave={e=>e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,.05)"}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px"}}>
                      <div>
                        <div style={{fontSize:15,fontWeight:700,color:"var(--gray-900)"}}>{mLabel} {r.year}</div>
                        <div style={{fontSize:13,color:r.bal>=0?"var(--green)":"var(--red)",fontWeight:600,marginTop:2,display:"flex",alignItems:"center",gap:4}}>
                          {r.bal>=0?<TrendingUp size={13}/>:<TrendingDown size={13}/>} {fmt(r.bal)}
                        </div>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:16}}>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:12,color:"var(--gray-400)"}}>{c.income_label||"Ingresos"}</div>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--green)"}}>{fmt(r.inc)}</div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:12,color:"var(--gray-400)"}}>{c.expense_label||"Gastos"}</div>
                          <div style={{fontSize:13,fontWeight:600,color:"var(--red)"}}>{fmt(r.exp)}</div>
                        </div>
                        <div style={{color:"var(--gray-400)",transition:"transform .2s",display:"flex",
                                     transform:isOpen?"rotate(180deg)":"rotate(0deg)"}}><ChevronDown size={16}/></div>
                      </div>
                    </div>
                    {isOpen && (
                      <div style={{borderTop:"1px solid var(--border)",padding:"14px 16px",
                                   display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,background:"var(--gray-50)"}}>
                        <div style={{background:"var(--card)",borderRadius:"var(--radius-sm)",padding:"10px 14px",border:"1px solid var(--border)"}}>
                          <div style={{fontSize:12,color:"var(--gray-400)",marginBottom:4}}>{c.balance_label||"Balance"}</div>
                          <div style={{fontSize:16,fontWeight:700,color:r.bal>=0?"var(--green)":"var(--red)"}}>{fmt(r.bal)}</div>
                        </div>
                        <div style={{background:"var(--card)",borderRadius:"var(--radius-sm)",padding:"10px 14px",border:"1px solid var(--border)"}}>
                          <div style={{fontSize:12,color:"var(--gray-400)",marginBottom:4}}>{c.fund_label||"Ahorro"} ({pct}%)</div>
                          <div style={{fontSize:16,fontWeight:700,color:"var(--purple)"}}>{fmt(r.inv)}</div>
                        </div>
                        <div style={{background:"var(--card)",borderRadius:"var(--radius-sm)",padding:"10px 14px",border:"1px solid var(--border)",gridColumn:"span 2"}}>
                          <div style={{fontSize:12,color:"var(--gray-400)",marginBottom:4}}>{lang==="en"?"Available":"Disponible"} ({100-pct}%)</div>
                          <div style={{fontSize:16,fontWeight:700,color:"var(--green)"}}>{fmt(r.lib)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Overlay invisible para cerrar el popover al clicar fuera */}
      {popover && (
        <div onClick={()=>setPopover(null)}
             style={{position:"fixed",inset:0,zIndex:998}} />
      )}

      {/* Popover de detalle: importe/mes clicado + botón para ir al historial */}
      {popover && (
        <div
          style={{
            position:"fixed",
            top: popover.y, left: popover.x,
            transform:"translate(-50%, -110%)",
            background:"var(--card)", border:"1px solid var(--border)",
            borderRadius:10, padding:"12px 14px", minWidth:180,
            boxShadow:"var(--shadow-md, 0 6px 20px rgba(0,0,0,.15))",
            zIndex:999,
          }}
          onClick={e=>e.stopPropagation()}>
          <div style={{fontSize:12,color:"var(--gray-500)",marginBottom:4}}>{popover.title}</div>
          <div style={{fontSize:18,fontWeight:700,color:popover.color,marginBottom:10}}>{popover.value}</div>
          <button className="btn btn-primary btn-sm" style={{width:"100%",justifyContent:"center"}}
                  onClick={()=>goToFiltered(popover.filter, popover.path)}>
            {lang==="en"?"View history →":"Ver historial →"}
          </button>
        </div>
      )}
    </div>
  );
}
